import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  approveAdminStaffRegistration,
  fetchPendingStaffRegistrations,
  rejectAdminStaffRegistration,
} from '../../api/admin';
import DashboardShell from '../../components/ui/DashboardShell';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import { useToast } from '../../context/ToastContext';
import { useState } from 'react';

const AdminPendingStaffPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-pending-staff', page, pageSize],
    queryFn: () => fetchPendingStaffRegistrations({ page, limit: pageSize }),
  });

  const approveMut = useMutation({
    mutationFn: approveAdminStaffRegistration,
    onSuccess: (res) => {
      showToast(res.message || t('auth.staffRegister.approved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['admin-pending-staff'] });
    },
  });

  const rejectMut = useMutation({
    mutationFn: rejectAdminStaffRegistration,
    onSuccess: (res) => {
      showToast(res.message || t('auth.staffRegister.rejected'), 'success');
      queryClient.invalidateQueries({ queryKey: ['admin-pending-staff'] });
    },
  });

  const rows = data?.data || [];

  return (
    <DashboardShell title={t('admin.staffRegistrations.title')} subtitle={t('admin.staffRegistrations.subtitle')}>
      {isLoading && <p className="text-zinc-500">{t('common.loading')}</p>}

      {!isLoading && rows.length === 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-10 text-center dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-zinc-500">{t('admin.staffRegistrations.empty')}</p>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((row) => (
          <article
            key={row.permissionId}
            className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900"
          >
            <div>
              <p className="font-semibold">
                {row.firstName} {row.lastName}
              </p>
              <p className="text-sm text-zinc-500">{row.email}</p>
              <p className="mt-1 text-sm">
                {t('admin.staffRegistrations.salon')}: <span className="font-medium">{row.salon?.businessName}</span>
              </p>
              <p className="text-xs text-zinc-500">
                {t('admin.staffRegistrations.requested')}: {new Date(row.requestedAt).toLocaleString()}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={rejectMut.isPending}
                onClick={() => rejectMut.mutate(row.permissionId)}
                className="rounded-lg border border-zinc-300 px-3 py-2 text-sm dark:border-zinc-700"
              >
                {t('common.reject')}
              </button>
              <button
                type="button"
                disabled={approveMut.isPending}
                onClick={() => approveMut.mutate(row.permissionId)}
                className="rounded-lg bg-glow-600 px-3 py-2 text-sm text-white hover:bg-glow-500"
              >
                {t('common.approve')}
              </button>
            </div>
          </article>
        ))}
      </div>

      <PaginatedFooter
        pagination={data?.pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </DashboardShell>
  );
};

export default AdminPendingStaffPage;
